import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { AuthState, User } from '../types';
import { AuthService } from '../services/authService';
import toast from 'react-hot-toast';

interface AuthContextType {
  state: AuthState;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => Promise<void>;
  checkEmailExists: (email: string) => Promise<boolean>;
  sendVerificationCode: (email: string) => Promise<void>;
  verifyEmailCode: (email: string, code: string) => Promise<boolean>;
  resetPassword: (email: string) => Promise<void>;
  confirmResetPassword: (token: string, newPassword: string) => Promise<void>;
}

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction =
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'REGISTER_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'REGISTER_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'AUTH_LOADED' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'INITIALIZE'; payload: { user: User | null; token: string | null } };

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_SUCCESS':
    case 'REGISTER_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'LOGIN_FAILURE':
    case 'REGISTER_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'AUTH_LOADED':
      return {
        ...state,
        isLoading: false,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'INITIALIZE':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: Boolean(action.payload.token),
        isLoading: false,
      };
    default:
      return state;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state from localStorage
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;

    dispatch({
      type: 'INITIALIZE',
      payload: { user, token }
    });
  }, []);

  const checkEmailExists = async (email: string): Promise<boolean> => {
    try {
      return await AuthService.checkEmailExists(email);
    } catch (error) {
      console.error('Error checking email:', error);
      return false;
    }
  };

  const sendVerificationCode = async (email: string): Promise<void> => {
    try {
      await AuthService.resetPassword(email);
      toast.success('Password reset instructions sent to your email');
    } catch (error) {
      toast.error('Failed to send reset instructions');
      throw error;
    }
  };

  const verifyEmailCode = async (email: string, code: string): Promise<boolean> => {
    return true;
  };

  const resetPassword = async (email: string): Promise<void> => {
    try {
      await AuthService.resetPassword(email);
      toast.success('Password reset instructions sent to your email');
    } catch (error) {
      toast.error('Failed to send reset instructions');
      throw error;
    }
  };

  const confirmResetPassword = async (token: string, newPassword: string): Promise<void> => {
    try {
      await AuthService.confirmResetPassword(token, newPassword);
      toast.success('Password has been reset successfully');
    } catch (error) {
      toast.error('Failed to reset password');
      throw error;
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const response = await AuthService.login(email, password);
      // Save tokens in HttpOnly cookies
      document.cookie = `token=${response.token}; path=/; HttpOnly`;
      document.cookie = `refreshToken=${response.refreshToken}; path=/; HttpOnly`;
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: response.user,
          token: response.token
        },
      });
      toast.success('Successfully logged in');
    } catch (error) {
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: error instanceof Error ? error.message : 'Login failed',
      });
      toast.error('Invalid email or password');
      throw error;
    }
  };

  const register = async (name: string, email: string, password: string) => {
    try {
      const response = await AuthService.register(name, email, password);
      // Save tokens in HttpOnly cookies
      document.cookie = `token=${response.token}; path=/; HttpOnly`;
      document.cookie = `refreshToken=${response.refreshToken}; path=/; HttpOnly`;
      
      dispatch({
        type: 'REGISTER_SUCCESS',
        payload: {
          user: response.user,
          token: response.token
        },
      });
      toast.success('Registration successful');
    } catch (error) {
      dispatch({
        type: 'REGISTER_FAILURE',
        payload: error instanceof Error ? error.message : 'Registration failed',
      });
      toast.error('Registration failed');
      throw error;
    }
  };

  const logout = async () => {
    try {
      await AuthService.logout();
      document.cookie = 'token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; HttpOnly';
      document.cookie = 'refreshToken=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; HttpOnly';
      dispatch({ type: 'LOGOUT' });
      toast.success('Successfully logged out');
    } catch (error) {
      console.error('Logout failed:', error);
      toast.error('Logout failed. Please try again.');
    }
  };

  const updateUser = async (userData: Partial<User>) => {
    try {
      if (!state.user) {
        throw new Error('User not authenticated');
      }
      
      const updatedUser = await AuthService.updateProfile(state.user.id, userData);
      
      dispatch({
        type: 'UPDATE_USER',
        payload: updatedUser,
      });
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
      throw error;
    }
  };

  return (
    <AuthContext.Provider 
      value={{ 
        state, 
        login, 
        register, 
        logout, 
        updateUser, 
        checkEmailExists,
        sendVerificationCode,
        verifyEmailCode,
        resetPassword,
        confirmResetPassword
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};